import ApiIcon from '@mui/icons-material/Api';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import HttpServices from 'src/services/httpService';
import { integrateModuleURL, userModuleURL } from 'src/services/urlService';
import Spinner from 'src/utils/Loader/spinner';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';

const GenerateAPIKeyComponent = () => {
  const { showToast } = useToaster();
  const { t: translate } = useTranslation();

  const [loader, setLoader] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [isAPIEnabled, setAPIEnabled] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [showContactPopup, setShowContactPopup] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const isCustomDomain = CommonHelper.CustomDomain();
  const isPortalAdmin = CommonHelper.GetBrandInfoFromLocalStorage("is_portal_admin");

  const userOrgDetails = CommonHelper.GetBrandInfoFromLocalStorage();
  useEffect(() => {
    getAPIKey();
  }, []);

  const getAPIKey = () => {
    const postProps = {
      url: userModuleURL.getAPIKey,
      successCallback: apiKeySuccessCallback,
      failureCallback,
    };
    HttpServices.Get(postProps);
  };

  const failureCallback = (message) => {
    setLoading(false);
    setLoader(false);
    message && showToast(message, 'e');
  };

  const apiKeySuccessCallback = (data, message) => {
    setLoader(false);
    setInitialLoad(false);
    setApiKey(data.api_key || '');
    setAPIEnabled(data.is_api_enabled ?? false);
    message && showToast(message, 's');
  };

  const handleGenerate = () => {
    setLoading(true);
    setLoader(true);
    const postProps = {
      body: {},
      url: userModuleURL.generateAPIKey,
      successCallback: generateAPISuccessCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
    setShowConfirm(false);
  };

  const handleReGenerate = () => setShowConfirm(true);
  const handleDelete = () => setShowDeleteConfirm(true);

  const confirmDelete = () => {
    setLoading(true);
    setLoader(true);
    const postProps = {
      url: userModuleURL.deleteAPIKey,
      successCallback: deleteSuccessCallback,
      failureCallback,
    };
    HttpServices.Get(postProps);
    setShowDeleteConfirm(false);
  };

  const deleteSuccessCallback = (data, message) => {
    setLoading(false);
    setLoader(false);
    setApiKey('');
    setShowKey(false);
    message &&
      showToast(message || translate('api_key_management.delete_key_success', 'API key deleted successfully'), 's');
  };

  const generateAPISuccessCallback = (data, message) => {
    setLoading(false);
    setLoader(false);
    setApiKey(data.api_key);
    setShowKey(true);
    message && showToast(message, 's');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(apiKey).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  const toggleKeyVisibility = () => setShowKey(!showKey);

  const handleEnableAPI = () => {
    setLoading(true);
    const postProps = {
      url: userModuleURL.enableAPI,
      successCallback: (data, message) => {
        setAPIEnabled(true);
        showToast(message || translate('api_key_management.api_enabled_success', 'API enabled successfully'), 's');
        setContactLoading(false);
        setShowContactPopup(false);
        window.location.reload();
      },
      failureCallback: (message) => {
        setContactLoading(false);
        showToast(message || translate('api_key_management.api_enable_fail', 'Failed to enable API'), 'e');
      },
    };
    HttpServices.Post(postProps);
  };

  const handleSendContactRequest = () => {
    setContactLoading(true);
    const postProps = {
      url: integrateModuleURL.handleContactUs,
      body: {
        subject: 'API Access Request',
        message: `Requesting lifetime API access - Org Id - ${userOrgDetails.org_id}`,
      },
      successCallback: () => handleEnableAPI(),
      failureCallback: (message) => {
        setContactLoading(false);
        showToast(message || translate('api_key_management.contact_fail', 'Failed to send request'), 'e');
      },
    };
    HttpServices.Post(postProps);
  };

  const handleOpenContact = () => {
    setShowContactPopup(true); // Direct open
  };

  const maskedKey = '••••••••••••••••••••••••••••••••';

  const renderApiDocsButton = () => (
    <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2, mb: 2 }}>
      {(!isCustomDomain || isPortalAdmin) && (<Button
        variant="contained"
        color="primary"
        size="medium"
        startIcon={<ApiIcon />}
        href="https://brand2social.com/api/auth-user"
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          fontWeight: 600,
          textTransform: 'none',
          borderRadius: 2,
          px: 3,
          py: 1,
        }}
      >
        {translate('api_key_management.api_docs', 'API Documentation')}
      </Button>)}

      {(!isCustomDomain || isPortalAdmin) && (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<YouTubeIcon />}
          onClick={() => window.open('https://www.youtube.com/watch?v=hUbRFBayhBE', '_blank')}
          sx={{ fontWeight: 600, textTransform: 'none' }}
        >
          {translate('common.tutorial', 'Tutorial')}
        </Button>
      )}
    </Stack>
  );

  /* ------------------------------------------------------------------ */
  /*  Main Render                                                       */
  /* ------------------------------------------------------------------ */

  // ---------- API NOT ENABLED ----------
  if (!isAPIEnabled) {
    return (
      <Card
        sx={{
          height: 'calc(100vh - 160px)',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 2,
        }}
      >
        <ApiIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: 'primary.main', textAlign: 'center' }}>
          {translate('api_key_management.api_not_enabled_title')}
        </Typography>
        <Typography variant="body1" sx={{ mb: 1, color: 'text.secondary', textAlign: 'center', maxWidth: 380 }}>
          {translate('api_key_management.api_not_enabled_body')}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleOpenContact}
          sx={{ fontWeight: 600, borderRadius: 1, minWidth: 200, marginBottom: 2 }}
        >
          {translate('api_key_management.contact_us')}
        </Button>

        {renderApiDocsButton()}

        {/* Contact Request Dialog */}
        <Dialog open={showContactPopup} onClose={() => setShowContactPopup(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, color: 'primary.main' }}>
            <ApiIcon sx={{ fontSize: 36, color: 'primary.main', mb: 1 }} />
            <div>{translate('api_key_management.contact_dialog_title')}</div>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {translate('api_key_management.contact_dialog_body')}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
            <Button
              onClick={() => setShowContactPopup(false)}
              variant="outlined"
              color="primary"
              sx={{ minWidth: 100, fontWeight: 600 }}
            >
              {translate('api_key_management.contact_dialog_cancel')}
            </Button>
            <Button
              onClick={handleSendContactRequest}
              color="primary"
              variant="contained"
              disabled={contactLoading}
              sx={{ minWidth: 120, fontWeight: 700 }}
            >
              {contactLoading
                ? translate('api_key_management.contact_dialog_sending')
                : translate('api_key_management.contact_dialog_send')}
            </Button>
          </DialogActions>
        </Dialog>
      </Card>
    );
  }

  // ---------- API ENABLED ----------
  return (
    <>
      <Card
        sx={{
          height: 'calc(100vh - 160px)',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        }}
      >
        <Stack direction="row" alignItems="left" justifyContent="" sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {translate('api_key_management.title')}
          </Typography>
        </Stack>
        <Helmet>
          <title>{translate('api_key_management.title')}</title>
        </Helmet>

        {/* Regenerate Confirmation */}
        <Dialog open={showConfirm} onClose={() => setShowConfirm(false)}>
          <DialogTitle>{translate('api_key_management.confirm_regenerate_title')}</DialogTitle>
          <DialogContent>
            <DialogContentText>{translate('api_key_management.confirm_regenerate_body')}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowConfirm(false)}>{translate('common.cancel')}</Button>
            <Button onClick={handleGenerate} autoFocus>
              {translate('api_key.confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
          <DialogTitle>{translate('api_key_management.confirm_delete_title')}</DialogTitle>
          <DialogContent>
            <DialogContentText>{translate('api_key_management.confirm_delete_body')}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteConfirm(false)}>{translate('common.cancel')}</Button>
            <Button onClick={confirmDelete} color="error" autoFocus>
              {translate('api_key_management.delete_key')}
            </Button>
          </DialogActions>
        </Dialog>

        {loader ? (
          <Spinner />
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <Card
              sx={{
                width: '100%',
                maxWidth: 600,
                p: 4,
                borderRadius: 2,
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                background: 'linear-gradient(to bottom right, #f8f9fa, #ffffff)',
              }}
            >
              <Typography
                variant="h5"
                gutterBottom
                sx={{ mb: 3, fontWeight: 600, color: 'primary.main', textAlign: 'center' }}
              >
                {translate('api_key_management.bearer_token')}
              </Typography>

              {apiKey ? (
                <>
                  <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                    {translate('api_key_management.your_api_key')}
                  </Typography>

                  <TextField
                    fullWidth
                    variant="outlined"
                    value={showKey ? apiKey : maskedKey}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip
                            title={
                              showKey ? translate('api_key_management.hide') : translate('api_key_management.show')
                            }
                          >
                            <IconButton onClick={toggleKeyVisibility}>
                              {showKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </Tooltip>

                          <Tooltip
                            title={
                              copySuccess
                                ? translate('api_key_management.copied')
                                : translate('api_key_management.copy')
                            }
                          >
                            <IconButton onClick={handleCopy}>
                              {copySuccess ? <CheckIcon color="success" /> : <ContentCopyIcon />}
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                        borderRadius: 1,
                      },
                    }}
                  />

                  <Typography
                    variant="body2"
                    sx={{
                      mb: 3,
                      p: 2,
                      backgroundColor: '#fff8e1',
                      borderRadius: 1,
                      borderLeft: '4px solid #ffc107',
                    }}
                  >
                    {translate('api_key_management.warning_keep_secure')}
                  </Typography>

                  {renderApiDocsButton()}
                </>
              ) : (
                !initialLoad && (
                  <>
                    <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', textAlign: 'center' }}>
                      {translate('api_key_management.no_key_yet')}
                    </Typography>
                    {renderApiDocsButton()}
                  </>
                )
              )}

              <Stack direction="row" spacing={2}>
                <LoadingButton
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="medium"
                  loading={loading}
                  onClick={apiKey ? handleReGenerate : handleGenerate}
                  sx={{
                    py: 1,
                    fontWeight: 700,
                    borderRadius: 2,
                    fontSize: 16,
                    letterSpacing: 0.5,
                    textTransform: 'none',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                    backgroundColor: 'primary.main',
                    color: '#fff',
                    transition: 'all 0.2s',
                    minWidth: 160,
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                      boxShadow: '0 4px 16px rgba(25,118,210,0.15)',
                    },
                  }}
                >
                  {apiKey
                    ? translate('api_key_management.regenerate_key')
                    : translate('api_key_management.generate_key')}
                </LoadingButton>

                {apiKey && (
                  <Button
                    variant="outlined"
                    color="error"
                    fullWidth
                    size="medium"
                    onClick={handleDelete}
                    startIcon={<DeleteIcon />}
                    sx={{
                      py: 1,
                      fontWeight: 700,
                      borderRadius: 2,
                      fontSize: 16,
                      letterSpacing: 0.5,
                      textTransform: 'none',
                      boxShadow: '0 1px 4px rgba(244,67,54,0.08)',
                      borderWidth: 2,
                      borderColor: 'error.main',
                      color: 'error.main',
                      minWidth: 160,
                      transition: 'all 0.2s',
                      '&:hover': {
                        backgroundColor: 'error.main',
                        color: '#fff',
                        boxShadow: '0 2px 8px rgba(244,67,54,0.15)',
                      },
                    }}
                  >
                    {translate('api_key_management.delete_key')}
                  </Button>
                )}
              </Stack>
            </Card>
          </Box>
        )}
      </Card>
    </>
  );
};

export default GenerateAPIKeyComponent;
